import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCampaignService } from '../services';
import { ContactCaptureOffer, ContactCaptureOfferRouteState } from '../types';

// Long enough for the Campaigns table to register as the landing page before the offer covers it.
export const CONTACT_CAPTURE_OFFER_DELAY_MS = 600;

type RouteOffer = NonNullable<ContactCaptureOfferRouteState['contactCaptureOffer']>;

export interface ContactCaptureOfferController {
  offer: ContactCaptureOffer | null;
  isOpen: boolean;
  accept: () => void;
  dismiss: () => void;
}

export const useContactCaptureOfferController = (): ContactCaptureOfferController => {
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const companyId = useSelector(getCurrentCompanyId) as string;
  const { getCampaignGroupNames } = useCampaignService();
  const routeOffer = (state as ContactCaptureOfferRouteState | null)?.contactCaptureOffer;
  const [pending, setPending] = useState<RouteOffer | null>(null);
  const [offer, setOffer] = useState<ContactCaptureOffer | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!routeOffer) return;
    setPending(routeOffer);
    // Cleared straight away so a refresh or a Back into this entry does not offer again.
    navigate({ pathname, search }, { replace: true, state: null });
  }, [routeOffer, navigate, pathname, search]);

  useEffect(() => {
    if (!pending) return;
    let alive = true;
    // The save response carries no group names, so they are read back while the delay runs.
    const delay = new Promise(resolve => setTimeout(resolve, CONTACT_CAPTURE_OFFER_DELAY_MS));
    Promise.all([getCampaignGroupNames(companyId, pending.campaignId), delay]).then(([groupNames]) => {
      if (!alive) return;
      setOffer({ ...pending, groupNames });
      setIsOpen(true);
    });
    return () => { alive = false; };
  }, [pending, companyId, getCampaignGroupNames]);

  const dismiss = useCallback(() => setIsOpen(false), []);

  const accept = useCallback(() => {
    if (!offer) return;
    setIsOpen(false);
    navigate(`/contact-capture/new?campaignId=${encodeURIComponent(offer.campaignId)}`);
  }, [offer, navigate]);

  return { offer, isOpen, accept, dismiss };
};
